import React, { useEffect, useMemo, useRef } from 'react'
import L from 'leaflet'
import {
  LayerGroup,
  LayersControl,
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents
} from 'react-leaflet'
import {
  GeoSearchControl,
  OpenStreetMapProvider
} from 'leaflet-geosearch'
import OlaProvider from './olaProvider'

// `leaflet-geosearch` doesn't export its result type publicly. Define
// the bits we actually consume — the event we listen to fires with
// `{ location: { x, y, label } }` where `x` = longitude, `y` = latitude.
interface GeoSearchResult {
  x: number;
  y: number;
  label: string;
}
interface GeoSearchShowLocationEvent {
  location: GeoSearchResult;
}
import 'leaflet/dist/leaflet.css'
import 'leaflet-geosearch/dist/geosearch.css'
import './LocationPicker.scss'
import {
  reverseGeocode,
  parseCoordinates,
  parsePlaceName,
  olaReverseGeocodeDetailed,
  ParsedAddress
} from './geocode'

// ── Marker icon ─────────────────────────────────────────────────────────
// Leaflet ships its marker as static images under `leaflet/dist/images/`.
// Bundlers don't always wire those paths automatically — explicit imports
// guarantee the bundler emits them and the marker actually renders.
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'

const defaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
})

export interface LocationValue {
  latitude: number;
  longitude: number;
  address?: string;
}

interface Props {
  /** Current coordinates + optional address. `null` = no pin yet. */
  value: LocationValue | null;
  onChange?: (value: LocationValue) => void;
  /**
   * When set, every picked point (search result, map click, or marker drag)
   * is reverse-geocoded and the structured address is handed back here — so
   * the caller can fill its country / state / district / taluka / pincode
   * fields from whatever the user picked on the map.
   */
  onAddressResolved?: (address: ParsedAddress) => void;
  /** When true, hides the search box + disables marker drag/map clicks. */
  readOnly?: boolean;
  height?: number;
  /**
   * When true, the user can drag the bottom edge to resize the map (like a
   * textarea). `height` becomes the initial height; the map re-renders to fit
   * via a ResizeObserver → `invalidateSize`.
   */
  resizable?: boolean;
  /** Where to centre the map before a pin is dropped. Defaults to India centroid. */
  defaultCenter?: { latitude: number; longitude: number };
  defaultZoom?: number;
  /** Optional label rendered above the map (e.g. "Project location"). */
  label?: React.ReactNode;
}

// India centroid — sane default that keeps the country in view without
// committing to any specific city.
const DEFAULT_CENTER = { latitude: 22.9734, longitude: 78.6569 }

// ── Internal: drop / move pin on click. Disabled in readOnly mode. ──────
const MapClickHandler: React.FC<{
  onPick: (lat: number, lng: number) => void;
}> = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng)
    }
  })
  return null
}

// ── Internal: recenter when `value` changes externally. ────────────────
const Recenter: React.FC<{ lat: number; lng: number; zoom: number }> = ({
  lat,
  lng,
  zoom
}) => {
  const map = useMap()
  useEffect(() => {
    map.setView([lat, lng], zoom)
  }, [lat, lng, zoom, map])
  return null
}

// ── Internal: a map control button that flies to the pin at full zoom. ──
// Built as an imperative L.Control so it sits in the standard Leaflet
// control corner (alongside +/−) and is unaffected by map pan/zoom.
const ZOOM_TO_PIN_LEVEL = 18

const ZoomToPin: React.FC<{ lat: number; lng: number }> = ({ lat, lng }) => {
  const map = useMap()
  // Keep the latest coords in a ref so the control is built once and never
  // torn down when the parent re-renders with new lat/lng.
  const posRef = useRef({ lat, lng })
  useEffect(() => {
    posRef.current = { lat, lng }
  }, [lat, lng])

  useEffect(() => {
    const ctrl = new L.Control({ position: 'topleft' })
    ctrl.onAdd = () => {
      const btn = L.DomUtil.create(
        'button',
        'leaflet-bar location-picker__zoom-pin'
      ) as HTMLButtonElement
      btn.type = 'button'
      btn.title = 'Zoom to project location'
      btn.setAttribute('aria-label', 'Zoom to project location')
      // Crosshair / target glyph.
      btn.innerHTML =
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="7"/><line x1="12" y1="1" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="23"/><line x1="1" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="23" y2="12"/><circle cx="12" cy="12" r="2" fill="currentColor"/></svg>'
      L.DomEvent.disableClickPropagation(btn)
      L.DomEvent.on(btn, 'click', (e) => {
        L.DomEvent.stop(e)
        map.flyTo(
          [posRef.current.lat, posRef.current.lng],
          ZOOM_TO_PIN_LEVEL
        )
      })
      return btn
    }
    ctrl.addTo(map)
    return () => {
      ctrl.remove()
    }
  }, [map])
  return null
}

// ── Internal: keep Leaflet's cached size in sync when its container is
//    resized — window resize, or the user dragging a `resizable` map. Without
//    this, growing the container leaves gray (un-rendered) tiles. ───────────
const AutoInvalidateSize: React.FC = () => {
  const map = useMap()
  useEffect(() => {
    const el = map.getContainer()
    const ro = new ResizeObserver(() => map.invalidateSize())
    ro.observe(el)
    return () => ro.disconnect()
  }, [map])
  return null
}

// ── Internal: mount the geosearch control. The control isn't a React
//    component in upstream's API — it's an L.Control instance that we add
//    via the imperative map handle. We listen to its `geosearch/showlocation`
//    event to lift the picked place into `onChange`.
//
// `onPick` is funnelled through a ref so the effect can keep `[map]` as
// its dep array. Otherwise every parent re-render (e.g. when formik
// pushes new lat/lng in) would recreate `onPick`, tear the control
// down, and re-mount it — which wipes whatever the user had typed /
// picked and clears the search input.
const SearchControl: React.FC<{
  onPick: (lat: number, lng: number, address?: string) => void;
}> = ({ onPick }) => {
  const map = useMap()
  const onPickRef = useRef(onPick)
  useEffect(() => {
    onPickRef.current = onPick
  }, [onPick])

  useEffect(() => {
    // Use Ola Maps when an API key is present (better Indian-address
    // coverage). Silently fall back to OSM Nominatim otherwise so devs
    // who clone the repo without the key still see a working picker.
    const provider = process.env.REACT_APP_OLA_MAPS_KEY
      ? new OlaProvider()
      : new OpenStreetMapProvider()
    const control = GeoSearchControl({
      provider: provider as unknown as Parameters<typeof GeoSearchControl>[0]['provider'],
      style: 'bar',
      showMarker: false,
      retainZoomLevel: false,
      autoClose: true,
      searchLabel: 'Search a place, address, or landmark',
      keepResult: true
    })
    map.addControl(control as unknown as L.Control)

    const handler = (event: GeoSearchShowLocationEvent) => {
      const { x, y, label } = event.location
      // Call through the ref so we always invoke the latest callback
      // without re-mounting the geosearch control.
      onPickRef.current(y, x, label)
    }
    // Geosearch fires a custom event whose payload isn't part of
    // leaflet's built-in event-name → type map, so cast via `unknown` to
    // satisfy `L.LeafletEventHandlerFn`.
    const leafletHandler = handler as unknown as L.LeafletEventHandlerFn
    map.on('geosearch/showlocation', leafletHandler)

    return () => {
      map.off('geosearch/showlocation', leafletHandler)
      map.removeControl(control as unknown as L.Control)
    }
    // `map` is stable for the lifetime of the MapContainer; `onPick`
    // intentionally stays out of deps and is invoked via the ref above
    // so re-renders don't tear down the search control.
  }, [map])
  return null
}

/**
 * LocationPicker — Leaflet map with an OpenStreetMap-Nominatim search
 * box (free, no API key) and a draggable marker. Designed for project /
 * plot location capture: user types an address, picks a result, the pin
 * lands, and `onChange` fires with `{ latitude, longitude, address }`.
 *
 * Read-only mode hides the search box and freezes the marker — used on
 * detail pages to show the saved location without letting the viewer
 * mutate it.
 */
const LocationPicker: React.FC<Props> = ({
  value,
  onChange,
  onAddressResolved,
  readOnly = false,
  height = 320,
  defaultCenter = DEFAULT_CENTER,
  defaultZoom = 5,
  label,
  resizable = false
}) => {
  const markerRef = useRef<L.Marker>(null)
  // Manual coordinate entry — the fallback for places that exist on Google
  // Maps but aren't in the search provider's POI index. Toggled open by the
  // "+" control; accepts a "lat, lng" paste or a Google Maps URL.
  const [manualOpen, setManualOpen] = React.useState(false)
  const [manualText, setManualText] = React.useState('')
  const [manualError, setManualError] = React.useState('')
  // Human-readable address shown in the editable Address field below the map.
  // Resolved automatically when a pin is placed (Ola reverse-geocode, plus the
  // place name from a pasted Google link) and editable so the user can match
  // the exact Google address. Whatever ends up here is what we persist.
  const [addressText, setAddressText] = React.useState(value?.address ?? '')
  // Flips once the address has been resolved or hand-edited, so a late async
  // `value.address` load (edit mode) can't clobber a user's own text.
  const addressDirtyRef = useRef(false)
  // Guards the async reverse-geocode callback from firing after unmount.
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  // Adopt an externally-loaded address (e.g. an existing project fetched in
  // edit mode) into the field — but only until the user/resolver touches it.
  useEffect(() => {
    if (!addressDirtyRef.current && value?.address) {
      setAddressText(value.address)
    }
  }, [value?.address])

  const pin = useMemo(() => {
    if (value && Number.isFinite(value.latitude) && Number.isFinite(value.longitude)) {
      return { lat: value.latitude, lng: value.longitude }
    }
    return null
  }, [value])

  const center = pin ?? { lat: defaultCenter.latitude, lng: defaultCenter.longitude }
  const zoom = pin ? 14 : defaultZoom

  // Push coords (and the best address we have right now) up to the caller.
  const pushChange = (lat: number, lng: number, address?: string) => {
    onChange?.({
      latitude: Number(lat.toFixed(6)),
      longitude: Number(lng.toFixed(6)),
      address: address || undefined
    })
  }

  // Resolve a freshly-placed pin and fill everything from it:
  //   • the human-readable address string (→ caller's address line)
  //   • the structured fields (pincode / state / district / taluka) via
  //     `onAddressResolved`, so e.g. the Pincode field auto-fills.
  // Ola first (India-accurate, carries the pincode), Nominatim as a fallback.
  // When the user pasted a Google link we prepend the place name it contained
  // (e.g. "Oceano — Malwan, Maharashtra"). A search pick already carries a
  // good label, so we keep that as the address string. Fire-and-forget: the
  // pin/coords are emitted first, so a slow geocode never blocks the pick.
  const resolveAndFill = async (
    lat: number,
    lng: number,
    opts?: { searchLabel?: string; placeName?: string | null }
  ) => {
    let parsed = await olaReverseGeocodeDetailed(lat, lng).catch(() => null)
    if (!parsed) parsed = await reverseGeocode(lat, lng).catch(() => null)
    if (!mountedRef.current) return

    // Address string for the caller's address line.
    let addr = opts?.searchLabel ?? ''
    if (!addr) {
      addr = opts?.placeName ?? ''
      if (parsed?.label) addr = addr ? `${addr} — ${parsed.label}` : parsed.label
    }
    if (addr) {
      addressDirtyRef.current = true
      setAddressText(addr)
      pushChange(lat, lng, addr)
    }

    // Structured fields (pincode, etc.) lifted to the caller.
    if (parsed && onAddressResolved) onAddressResolved(parsed)
  }

  const emit = (
    lat: number,
    lng: number,
    opts?: { searchLabel?: string; placeName?: string | null }
  ) => {
    // Emit immediately with the best address on hand so the pin/coords are
    // never blocked on geocoding.
    const immediate = opts?.searchLabel || opts?.placeName || addressText
    pushChange(lat, lng, immediate)
    void resolveAndFill(lat, lng, opts)
  }

  // Parse the manual paste (coords or Google Maps link) and drop the pin.
  // Same `emit` path as search / click / drag, so it recenters the map and
  // resolves the address just like any other pick — and carries the place
  // name out of a pasted Google link.
  const applyManual = () => {
    const parsed = parseCoordinates(manualText)
    if (!parsed) {
      setManualError(
        "Couldn't read a location — paste \"lat, lng\" or a Google Maps link."
      )
      return
    }
    setManualError('')
    emit(parsed.lat, parsed.lng, { placeName: parsePlaceName(manualText) })
    setManualText('')
    setManualOpen(false)
  }

  return (
    <div className="location-picker">
      {label && <div className="location-picker__label">{label}</div>}

      {/* Manual coordinate entry — fallback for places that are on Google
          Maps but missing from the search provider's POI index. The user
          pastes the coordinate (or the Google Maps URL) and the pin drops
          exactly. */}
      {!readOnly && (
        <div className="location-picker__manual">
          <button
            type="button"
            className="location-picker__manual-toggle"
            onClick={() => {
              setManualOpen((o) => !o)
              setManualError('')
            }}
            aria-expanded={manualOpen}
          >
            <span className="location-picker__manual-plus" aria-hidden>+</span>
            Can&apos;t find it? Enter coordinates manually
          </button>

          {manualOpen && (
            <div className="location-picker__manual-row">
              <input
                className="location-picker__manual-input"
                placeholder='Paste "lat, lng" or a Google Maps link'
                value={manualText}
                onChange={(e) => {
                  setManualText(e.target.value)
                  if (manualError) setManualError('')
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    applyManual()
                  }
                }}
              />
              <button
                type="button"
                className="location-picker__manual-apply"
                onClick={applyManual}
              >
                Drop pin
              </button>
            </div>
          )}

          {manualError && (
            <div className="location-picker__manual-error">{manualError}</div>
          )}
        </div>
      )}

      <div
        className="location-picker__map-wrap"
        style={
          resizable
            ? { height, minHeight: 300, resize: 'vertical', overflow: 'hidden' }
            : { height }
        }
      >
        <MapContainer
          center={[center.lat, center.lng]}
          zoom={zoom}
          scrollWheelZoom
          attributionControl={false}
          className="location-picker__map"
          // Keep the camera on a single world copy — combined with `noWrap`
          // on the tile layers this stops the map from repeating sideways
          // (and from panning into empty gray space) at low zoom.
          minZoom={2}
          maxBounds={[
            [-90, -180],
            [90, 180]
          ]}
          maxBoundsViscosity={1.0}
          worldCopyJump={false}
        >
          {/* Re-render Leaflet tiles when the map is resized (drag / window). */}
          <AutoInvalidateSize />

          {/* Street / Satellite basemap switch (top-right), mirroring
              Google's Map/Satellite toggle. Both are free, key-less raster
              tiles:
                • Street    — CARTO Voyager (colourful, labelled, Google-like)
                • Satellite — Esri World Imagery + a transparent Esri labels
                              overlay so place / road names show over aerial. */}
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Street">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png"
                subdomains="abcd"
                maxZoom={20}
                noWrap
                detectRetina
              />
            </LayersControl.BaseLayer>

            <LayersControl.BaseLayer name="Satellite">
              <LayerGroup>
                {/* Esri's free aerial only has imagery to ~z18 in rural areas
                    (deeper tiles return a "Map data not yet available"
                    placeholder). `maxNativeZoom` stops requesting past that and
                    lets Leaflet upscale the z18 tile instead — slightly soft at
                    z19–20 but continuous, with no missing-tile placeholders. */}
                <TileLayer
                  attribution='Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community'
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={20}
                  maxNativeZoom={18}
                  noWrap
                />
                {/* Transparent overlay: town / road / place labels on top of
                    the aerial imagery so the satellite view stays readable. */}
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                  maxZoom={20}
                  maxNativeZoom={18}
                  noWrap
                />
              </LayerGroup>
            </LayersControl.BaseLayer>
          </LayersControl>

          {!readOnly && (
            <SearchControl
              onPick={(lat, lng, label) => emit(lat, lng, { searchLabel: label })}
            />
          )}
          {!readOnly && (
            <MapClickHandler onPick={(lat, lng) => emit(lat, lng)} />
          )}

          {pin && (
            <Marker
              position={[pin.lat, pin.lng]}
              icon={defaultIcon}
              draggable={!readOnly}
              ref={markerRef}
              eventHandlers={{
                dragend: () => {
                  const m = markerRef.current
                  if (!m) return
                  const { lat, lng } = m.getLatLng()
                  emit(lat, lng)
                }
              }}
            />
          )}

          {pin && <Recenter lat={pin.lat} lng={pin.lng} zoom={14} />}
          {pin && <ZoomToPin lat={pin.lat} lng={pin.lng} />}
        </MapContainer>
      </div>

      {pin && (
        <div className="location-picker__coords">
          <span>
            <strong>Lat:</strong> {pin.lat.toFixed(6)}
          </span>
          <span>
            <strong>Lng:</strong> {pin.lng.toFixed(6)}
          </span>
          {/* Read-only viewers (detail page) get the saved address inline.
              The editable field below covers it in create/edit. */}
          {readOnly && value?.address && (
            <span className="location-picker__address" title={value.address}>
              {value.address}
            </span>
          )}
        </div>
      )}

      {!pin && !readOnly && (
        <div className="location-picker__hint">
          Search a place above or click anywhere on the map to drop a pin.
        </div>
      )}
    </div>
  )
}

export default LocationPicker
